// 17 September 2026: which pnl_metric_registry rows the project P&L tab's
// manual entry band may type into.
//
// Run: deno test --allow-read src/features/pnlAnalytics/manualEntryRegistryFilter.test.ts
// Run: npm run verify:pnl-manual-entry-registry-filter
//
// WHY THIS MOVED OUT OF THE HOOK. Labour hours stopped being payroll derived
// (20260917000100_pnl_manual_labour_hours.sql). The two canonical keys,
// labour.ordinary_hours and labour.overtime_hours, are SYSTEM rows:
// organization_id IS NULL, and neither matches 'operational.custom.%'. The
// band's old registry query narrowed on exactly those two things, so after
// that migration deactivates Ashleigh's duplicate custom rows there would be
// no row anywhere on the screen to type hours into. Widening the query is
// what keeps hours enterable at all.
//
// WHY A PURE MODULE AND NOT AN INLINE .or(). PostgREST's `or` is an escape
// hatch: postgrest-js appends the string verbatim (dist/index.mjs:785-789,
// @supabase/postgrest-js 2.88.0), so a typo does not fail, it silently
// selects a different set of rows. Two things guard that here. The filter is
// built from a validated UUID rather than interpolated blind, and
// `admitsManualEntryRow` re-checks every returned row against the same rule
// client side, so a filter that widened by accident cannot put another
// organisation's metric on the screen.
//
// SCOPE: this module owns only the organisation-versus-system alternation,
// which is the part expressed as raw PostgREST. metric_role, kind and
// is_active stay ordinary .eq() filters on the query and are not re-checked
// here.

/** Every metric an organisation creates in the operational metric editor carries this prefix (customMetricKey.ts). */
export const CUSTOM_OPERATIONAL_PREFIX = "operational.custom.";

/** The columns the band's registry query must select for `admitsManualEntryRow` to be able to decide. */
export interface ManualEntryRegistryRow {
  metric_key: string;
  /** Null marks a system row, seeded by migration and shared by every organisation. */
  organization_id: string | null;
  /** pnl_metric_registry.is_manual_input, true on exactly the two canonical labour hours keys. */
  is_manual_input?: boolean | null;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The PostgREST `or` expression for the band's registry query, or null when
 * `orgId` is not a plain UUID.
 *
 * Null rather than a thrown error, and null rather than a system-only
 * fallback: the caller turns it into a visible load failure. Quietly
 * dropping the organisation arm would render a band missing the rows the
 * user created, which reads as data loss.
 *
 * `*` rather than `%` is PostgREST's own spelling for a wildcard inside a
 * logical operator, and avoids a percent-encoded literal in the query string.
 */
export function manualEntryRegistryFilter(orgId: string): string | null {
  if (!UUID.test(orgId)) return null;
  return [
    `and(organization_id.eq.${orgId},metric_key.like.${CUSTOM_OPERATIONAL_PREFIX}*)`,
    "and(organization_id.is.null,is_manual_input.is.true)",
  ].join(",");
}

/**
 * Whether one returned registry row is genuinely one this organisation may
 * type into: its own custom operational metric, or a system key the database
 * has flagged as manual input.
 *
 * The flag has to be read as strictly true. A row selected before
 * 20260917000100 applies carries no `is_manual_input` column at all, and
 * `undefined` there must refuse rather than admit.
 */
export function admitsManualEntryRow(row: ManualEntryRegistryRow, orgId: string): boolean {
  if (row.organization_id === null) return row.is_manual_input === true;
  return row.organization_id === orgId && row.metric_key.startsWith(CUSTOM_OPERATIONAL_PREFIX);
}

/**
 * Sort key for the band: the canonical hours rows first, then the
 * organisation's own metrics, each by the registry's sort_order.
 *
 * System first is not cosmetic. Total Labour Hours is rendered directly
 * under the two rows it adds up, and a custom metric sorting between them
 * would put a count in the middle of a labour hours subtotal.
 */
export function manualMetricSortGroup(row: ManualEntryRegistryRow): number {
  return row.organization_id === null ? 0 : 1;
}
